<template>
  <div id="closet-clothing-form">
    <div class="page-header">
      <div class="title">{{ isEditMode ? "의류 수정" : "의류 등록" }}</div>
      <div class="subtitle">브랜드·모델·사이즈와 실측·핏을 기록하면 다음 구매 때 그대로 참고할 수 있어요</div>
    </div>
    <div class="clothing-layout">
      <!-- 좌측: 기존 폼 -->
      <div class="clothing-form-section">
        <div class="form-section">
          <div class="form-title">
            <span>{{ isEditMode ? "의류 수정" : "의류 등록" }}</span>
          </div>

          <div class="form-row">
            <div class="form-group sm">
              <label>카테고리</label>
              <select v-model="clothingForm.category" @change="onCategoryChange">
                <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
              </select>
            </div>
            <div class="form-group sm">
              <label>소분류</label>
              <select v-model="clothingForm.subCategory">
                <option value="">직접입력</option>
                <option v-for="sub in currentSubCategories" :key="sub" :value="sub">{{ sub }}</option>
              </select>
            </div>
            <div class="form-group" v-if="clothingForm.subCategory === ''">
              <label>소분류 입력</label>
              <input type="text" v-model="customSubCategory" placeholder="소분류 직접 입력" maxlength="50" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group sm">
              <label>브랜드</label>
              <input type="text" v-model="clothingForm.brandName" placeholder="브랜드명" maxlength="100" />
            </div>
            <div class="form-group">
              <label>제품명</label>
              <input type="text" v-model="clothingForm.productName" placeholder="제품명" maxlength="200" />
            </div>
            <div class="form-group sm">
              <label>표기 사이즈</label>
              <input type="text" v-model="clothingForm.labeledSize" placeholder="M, L, 95 등" maxlength="20" />
            </div>
            <div class="form-group sm">
              <label>구매일</label>
              <input type="date" v-model="clothingForm.purchaseDate" />
            </div>
          </div>

          <!-- 실측 치수 -->
          <div class="measure-section" v-if="currentMeasureFields.length > 0">
            <div class="measure-title">실측 치수</div>
            <div class="form-row">
              <div class="form-group sm" v-for="field in currentMeasureFields" :key="field.key">
                <label>{{ field.label }} ({{ field.unit }}) <span v-if="CLOTHING_MEASURE_HELP[field.key]" class="help-icon" :data-tooltip="CLOTHING_MEASURE_HELP[field.key]">?</span></label>
                <input type="number" v-model.number="clothingForm.measurements[field.key]"
                  :placeholder="field.label" step="0.1" />
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group sm">
              <label>만족도</label>
              <div class="star-rating">
                <span v-for="s in 5" :key="s"
                  class="star" :class="{ active: clothingForm.satisfaction != null && s <= clothingForm.satisfaction }"
                  @click="clothingForm.satisfaction = s">&#9733;</span>
              </div>
            </div>
            <div class="form-group fit-group">
              <label>핏 평가</label>
              <div class="fit-seg">
                <button
                  type="button"
                  v-for="opt in fitFeedbackOptions"
                  :key="opt.value"
                  class="fit-seg-btn"
                  :class="[fitClass(opt.value), { on: clothingForm.fitFeedback === opt.value }]"
                  @click="clothingForm.fitFeedback = clothingForm.fitFeedback === opt.value ? '' : opt.value"
                >{{ opt.label }}</button>
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group wide">
              <label>메모</label>
              <textarea v-model="clothingForm.memo" placeholder="메모" maxlength="500"></textarea>
            </div>
          </div>

          <div class="form-actions">
            <button class="btn-secondary" @click="goBack">취소</button>
            <button class="btn-primary" @click="submitClothing">{{ isEditMode ? "수정" : "등록" }}</button>
          </div>
        </div>
      </div>

      <!-- 우측: SVG 의류 도형 -->
      <div class="clothing-svg-section">
        <div class="svg-card">
          <div class="svg-title">의류 치수 다이어그램</div>
          <div class="svg-container">
            <!-- TOP / OUTER -->
            <svg v-if="svgCategory === 'TOP'" viewBox="0 0 300 360" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="topGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#dce8f5"/>
                  <stop offset="100%" stop-color="#eef4fc"/>
                </linearGradient>
              </defs>
              <!-- 상의 flat-lay -->
              <path :d="topSvgPath" fill="url(#topGrad)" stroke="#4a90d9" stroke-width="2" stroke-linejoin="round"/>
              <!-- 총장 (왼쪽 세로) -->
              <g v-if="m.total_length">
                <line :x1="topCoords.leftEdge - 20" :y1="topCoords.topY" :x2="topCoords.leftEdge - 20" :y2="topCoords.bottomY" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.leftEdge - 24" :y1="topCoords.topY" :x2="topCoords.leftEdge - 16" :y2="topCoords.topY" stroke="#888" stroke-width="1"/>
                <line :x1="topCoords.leftEdge - 24" :y1="topCoords.bottomY" :x2="topCoords.leftEdge - 16" :y2="topCoords.bottomY" stroke="#888" stroke-width="1"/>
                <rect :x="topCoords.leftEdge - 50" :y="(topCoords.topY + topCoords.bottomY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="topCoords.leftEdge - 22" :y="(topCoords.topY + topCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label">총장 {{ m.total_length }}cm</text>
              </g>
              <!-- 어깨너비 (상단 가로) -->
              <g v-if="m.shoulder_width">
                <line :x1="topCoords.shoulderL" :y1="topCoords.topY - 16" :x2="topCoords.shoulderR" :y2="topCoords.topY - 16" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.shoulderL" :y1="topCoords.topY - 20" :x2="topCoords.shoulderL" :y2="topCoords.topY - 12" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="topCoords.shoulderR" :y1="topCoords.topY - 20" :x2="topCoords.shoulderR" :y2="topCoords.topY - 12" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="150 - 34" :y="topCoords.topY - 34" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" :y="topCoords.topY - 23" text-anchor="middle" class="svg-measure-label red">어깨 {{ m.shoulder_width }}cm</text>
              </g>
              <!-- 가슴단면 (가슴 위치 가로) -->
              <g v-if="m.chest_width">
                <line :x1="topCoords.chestL" :y1="topCoords.chestY" :x2="topCoords.chestR" :y2="topCoords.chestY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.chestL" :y1="topCoords.chestY - 4" :x2="topCoords.chestL" :y2="topCoords.chestY + 4" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="topCoords.chestR" :y1="topCoords.chestY - 4" :x2="topCoords.chestR" :y2="topCoords.chestY + 4" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="(topCoords.chestL + topCoords.chestR) / 2 - 34" :y="topCoords.chestY + 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="(topCoords.chestL + topCoords.chestR) / 2" :y="topCoords.chestY + 20" text-anchor="middle" class="svg-measure-label red">가슴 {{ m.chest_width }}cm</text>
              </g>
              <!-- 소매길이 (우측 대각선) -->
              <g v-if="m.sleeve_length">
                <line :x1="topCoords.shoulderR" :y1="topCoords.topY" :x2="topCoords.sleeveEndX" :y2="topCoords.sleeveEndY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="(topCoords.shoulderR + topCoords.sleeveEndX) / 2 - 34" :y="(topCoords.topY + topCoords.sleeveEndY) / 2 - 18" width="68" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="(topCoords.shoulderR + topCoords.sleeveEndX) / 2" :y="(topCoords.topY + topCoords.sleeveEndY) / 2 - 6" text-anchor="middle" class="svg-measure-label blue">소매 {{ m.sleeve_length }}cm</text>
              </g>
              <!-- 밑단단면 (하단 가로) -->
              <g v-if="m.hem_width">
                <line :x1="topCoords.hemL" :y1="topCoords.bottomY + 12" :x2="topCoords.hemR" :y2="topCoords.bottomY + 12" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="topCoords.hemL" :y1="topCoords.bottomY + 8" :x2="topCoords.hemL" :y2="topCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <line :x1="topCoords.hemR" :y1="topCoords.bottomY + 8" :x2="topCoords.hemR" :y2="topCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <rect :x="150 - 30" :y="topCoords.bottomY + 18" width="60" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text x="150" :y="topCoords.bottomY + 30" text-anchor="middle" class="svg-measure-label green">밑단 {{ m.hem_width }}cm</text>
              </g>
              <!-- 암홀 (소매 두께: 어깨→겨드랑이 수직거리) -->
              <g v-if="m.armhole">
                <line :x1="topCoords.shoulderR" :y1="topCoords.topY" :x2="topCoords.rArmX" :y2="topCoords.rArmY" stroke="#8e44ad" stroke-width="0.8" stroke-dasharray="4,2"/>
                <circle :cx="topCoords.shoulderR" :cy="topCoords.topY" r="2" fill="#8e44ad"/>
                <circle :cx="topCoords.rArmX" :cy="topCoords.rArmY" r="2" fill="#8e44ad"/>
                <rect :x="topCoords.shoulderR - 68" :y="(topCoords.topY + topCoords.rArmY) / 2 - 8" width="62" height="16" rx="3" fill="#fff" stroke="#d8c0f0" stroke-width="0.5"/>
                <text :x="topCoords.shoulderR - 37" :y="(topCoords.topY + topCoords.rArmY) / 2 + 4" text-anchor="middle" class="svg-measure-label purple">암홀 {{ m.armhole }}cm</text>
              </g>
            </svg>

            <!-- BOTTOM -->
            <svg v-else-if="svgCategory === 'BOTTOM'" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="bottomGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#dce8f5"/>
                  <stop offset="100%" stop-color="#eef4fc"/>
                </linearGradient>
              </defs>
              <!-- 바지 flat-lay -->
              <path :d="bottomSvgPath" fill="url(#bottomGrad)" stroke="#4a90d9" stroke-width="2" stroke-linejoin="round"/>
              <!-- 총장 (왼쪽 세로) -->
              <g v-if="m.total_length">
                <line :x1="btmCoords.leftEdge - 20" :y1="btmCoords.topY" :x2="btmCoords.leftEdge - 20" :y2="btmCoords.bottomY" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.leftEdge - 24" :y1="btmCoords.topY" :x2="btmCoords.leftEdge - 16" :y2="btmCoords.topY" stroke="#888" stroke-width="1"/>
                <line :x1="btmCoords.leftEdge - 24" :y1="btmCoords.bottomY" :x2="btmCoords.leftEdge - 16" :y2="btmCoords.bottomY" stroke="#888" stroke-width="1"/>
                <rect :x="btmCoords.leftEdge - 50" :y="(btmCoords.topY + btmCoords.bottomY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="btmCoords.leftEdge - 22" :y="(btmCoords.topY + btmCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label">총장 {{ m.total_length }}cm</text>
              </g>
              <!-- 허리단면 (상단 가로) -->
              <g v-if="m.waist_width">
                <line :x1="btmCoords.waistL" :y1="btmCoords.topY - 14" :x2="btmCoords.waistR" :y2="btmCoords.topY - 14" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.waistL" :y1="btmCoords.topY - 18" :x2="btmCoords.waistL" :y2="btmCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="btmCoords.waistR" :y1="btmCoords.topY - 18" :x2="btmCoords.waistR" :y2="btmCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="150 - 34" :y="btmCoords.topY - 32" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" :y="btmCoords.topY - 21" text-anchor="middle" class="svg-measure-label red">허리 {{ m.waist_width }}cm</text>
              </g>
              <!-- 엉덩이단면 -->
              <g v-if="m.hip_width">
                <line :x1="btmCoords.hipL" :y1="btmCoords.hipY" :x2="btmCoords.hipR" :y2="btmCoords.hipY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.hipL" :y1="btmCoords.hipY - 4" :x2="btmCoords.hipL" :y2="btmCoords.hipY + 4" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="btmCoords.hipR" :y1="btmCoords.hipY - 4" :x2="btmCoords.hipR" :y2="btmCoords.hipY + 4" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="btmCoords.hipR + 6" :y="btmCoords.hipY - 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="btmCoords.hipR + 40" :y="btmCoords.hipY + 4" text-anchor="middle" class="svg-measure-label red">엉덩이 {{ m.hip_width }}cm</text>
              </g>
              <!-- 허벅지단면 -->
              <g v-if="m.thigh_width">
                <line :x1="btmCoords.thighL" :y1="btmCoords.thighY" :x2="btmCoords.thighMid" :y2="btmCoords.thighY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.thighL" :y1="btmCoords.thighY - 4" :x2="btmCoords.thighL" :y2="btmCoords.thighY + 4" stroke="#3498db" stroke-width="1"/>
                <line :x1="btmCoords.thighMid" :y1="btmCoords.thighY - 4" :x2="btmCoords.thighMid" :y2="btmCoords.thighY + 4" stroke="#3498db" stroke-width="1"/>
                <rect :x="btmCoords.thighL - 4" :y="btmCoords.thighY + 6" width="72" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="btmCoords.thighL + 32" :y="btmCoords.thighY + 18" text-anchor="middle" class="svg-measure-label blue">허벅지 {{ m.thigh_width }}cm</text>
              </g>
              <!-- 밑위 (허리~가랑이 세로) -->
              <g v-if="m.rise">
                <line :x1="btmCoords.waistR + 16" :y1="btmCoords.topY" :x2="btmCoords.waistR + 16" :y2="btmCoords.crotchY" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.waistR + 12" :y1="btmCoords.topY" :x2="btmCoords.waistR + 20" :y2="btmCoords.topY" stroke="#27ae60" stroke-width="1"/>
                <line :x1="btmCoords.waistR + 12" :y1="btmCoords.crotchY" :x2="btmCoords.waistR + 20" :y2="btmCoords.crotchY" stroke="#27ae60" stroke-width="1"/>
                <rect :x="btmCoords.waistR + 22" :y="(btmCoords.topY + btmCoords.crotchY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text :x="btmCoords.waistR + 50" :y="(btmCoords.topY + btmCoords.crotchY) / 2 + 4" text-anchor="middle" class="svg-measure-label green">밑위 {{ m.rise }}cm</text>
              </g>
              <!-- 밑단단면 -->
              <g v-if="m.hem_width">
                <line :x1="btmCoords.hemL" :y1="btmCoords.bottomY + 12" :x2="btmCoords.hemMid" :y2="btmCoords.bottomY + 12" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="btmCoords.hemL" :y1="btmCoords.bottomY + 8" :x2="btmCoords.hemL" :y2="btmCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <line :x1="btmCoords.hemMid" :y1="btmCoords.bottomY + 8" :x2="btmCoords.hemMid" :y2="btmCoords.bottomY + 16" stroke="#27ae60" stroke-width="1"/>
                <rect :x="btmCoords.hemL - 4" :y="btmCoords.bottomY + 20" width="60" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text :x="btmCoords.hemL + 26" :y="btmCoords.bottomY + 32" text-anchor="middle" class="svg-measure-label green">밑단 {{ m.hem_width }}cm</text>
              </g>
            </svg>

            <!-- ONEPIECE -->
            <svg v-else-if="svgCategory === 'ONEPIECE'" viewBox="0 0 300 400" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="opGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stop-color="#f0dce8"/>
                  <stop offset="100%" stop-color="#fceef4"/>
                </linearGradient>
              </defs>
              <path :d="onepieceSvgPath" fill="url(#opGrad)" stroke="#c0608a" stroke-width="2" stroke-linejoin="round"/>
              <!-- 총장 -->
              <g v-if="m.total_length">
                <line :x1="opCoords.leftEdge - 20" :y1="opCoords.topY" :x2="opCoords.leftEdge - 20" :y2="opCoords.bottomY" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="opCoords.leftEdge - 24" :y1="opCoords.topY" :x2="opCoords.leftEdge - 16" :y2="opCoords.topY" stroke="#888" stroke-width="1"/>
                <line :x1="opCoords.leftEdge - 24" :y1="opCoords.bottomY" :x2="opCoords.leftEdge - 16" :y2="opCoords.bottomY" stroke="#888" stroke-width="1"/>
                <rect :x="opCoords.leftEdge - 50" :y="(opCoords.topY + opCoords.bottomY) / 2 - 8" width="56" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="opCoords.leftEdge - 22" :y="(opCoords.topY + opCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label">총장 {{ m.total_length }}cm</text>
              </g>
              <!-- 어깨너비 -->
              <g v-if="m.shoulder_width">
                <line :x1="opCoords.shoulderL" :y1="opCoords.topY - 14" :x2="opCoords.shoulderR" :y2="opCoords.topY - 14" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="opCoords.shoulderL" :y1="opCoords.topY - 18" :x2="opCoords.shoulderL" :y2="opCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="opCoords.shoulderR" :y1="opCoords.topY - 18" :x2="opCoords.shoulderR" :y2="opCoords.topY - 10" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="150 - 34" :y="opCoords.topY - 32" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text x="150" :y="opCoords.topY - 21" text-anchor="middle" class="svg-measure-label red">어깨 {{ m.shoulder_width }}cm</text>
              </g>
              <!-- 가슴단면 -->
              <g v-if="m.chest_width">
                <line :x1="opCoords.chestL" :y1="opCoords.chestY" :x2="opCoords.chestR" :y2="opCoords.chestY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.chestR + 6" :y="opCoords.chestY - 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="opCoords.chestR + 40" :y="opCoords.chestY + 4" text-anchor="middle" class="svg-measure-label red">가슴 {{ m.chest_width }}cm</text>
              </g>
              <!-- 허리단면 -->
              <g v-if="m.waist_width">
                <line :x1="opCoords.waistL" :y1="opCoords.waistY" :x2="opCoords.waistR" :y2="opCoords.waistY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.waistR + 6" :y="opCoords.waistY - 8" width="68" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="opCoords.waistR + 40" :y="opCoords.waistY + 4" text-anchor="middle" class="svg-measure-label blue">허리 {{ m.waist_width }}cm</text>
              </g>
              <!-- 엉덩이단면 -->
              <g v-if="m.hip_width">
                <line :x1="opCoords.hipL" :y1="opCoords.hipY" :x2="opCoords.hipR" :y2="opCoords.hipY" stroke="#27ae60" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.hipR + 6" :y="opCoords.hipY - 8" width="72" height="16" rx="3" fill="#fff" stroke="#b0d8b0" stroke-width="0.5"/>
                <text :x="opCoords.hipR + 42" :y="opCoords.hipY + 4" text-anchor="middle" class="svg-measure-label green">엉덩이 {{ m.hip_width }}cm</text>
              </g>
              <!-- 소매길이 -->
              <g v-if="m.sleeve_length">
                <line :x1="opCoords.shoulderR" :y1="opCoords.topY" :x2="opCoords.sleeveEndX" :y2="opCoords.sleeveEndY" stroke="#3498db" stroke-width="0.8" stroke-dasharray="4,2"/>
                <rect :x="opCoords.sleeveEndX - 4" :y="opCoords.sleeveEndY + 4" width="68" height="16" rx="3" fill="#fff" stroke="#c0d8f0" stroke-width="0.5"/>
                <text :x="opCoords.sleeveEndX + 30" :y="opCoords.sleeveEndY + 16" text-anchor="middle" class="svg-measure-label blue">소매 {{ m.sleeve_length }}cm</text>
              </g>
            </svg>

            <!-- SHOES -->
            <svg v-else-if="svgCategory === 'SHOES'" viewBox="0 0 300 200" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="shoeGrad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stop-color="#e8e0d4"/>
                  <stop offset="100%" stop-color="#f5f0e8"/>
                </linearGradient>
              </defs>
              <!-- 신발 옆면 실루엣 -->
              <path :d="shoesSvgPath" fill="url(#shoeGrad)" stroke="#8a7560" stroke-width="2" stroke-linejoin="round"/>
              <!-- 발길이 -->
              <g v-if="m.foot_length">
                <line :x1="shoeCoords.toeX" :y1="shoeCoords.bottomY + 16" :x2="shoeCoords.heelX" :y2="shoeCoords.bottomY + 16" stroke="#888" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="shoeCoords.toeX" :y1="shoeCoords.bottomY + 12" :x2="shoeCoords.toeX" :y2="shoeCoords.bottomY + 20" stroke="#888" stroke-width="1"/>
                <line :x1="shoeCoords.heelX" :y1="shoeCoords.bottomY + 12" :x2="shoeCoords.heelX" :y2="shoeCoords.bottomY + 20" stroke="#888" stroke-width="1"/>
                <rect :x="(shoeCoords.toeX + shoeCoords.heelX) / 2 - 34" :y="shoeCoords.bottomY + 22" width="68" height="16" rx="3" fill="#fff" stroke="#ddd" stroke-width="0.5"/>
                <text :x="(shoeCoords.toeX + shoeCoords.heelX) / 2" :y="shoeCoords.bottomY + 34" text-anchor="middle" class="svg-measure-label">발길이 {{ m.foot_length }}mm</text>
              </g>
              <!-- 발볼너비 -->
              <g v-if="m.ball_width">
                <line :x1="shoeCoords.ballX" :y1="shoeCoords.ballTopY" :x2="shoeCoords.ballX" :y2="shoeCoords.bottomY" stroke="#e74c3c" stroke-width="0.8" stroke-dasharray="4,2"/>
                <line :x1="shoeCoords.ballX - 4" :y1="shoeCoords.ballTopY" :x2="shoeCoords.ballX + 4" :y2="shoeCoords.ballTopY" stroke="#e74c3c" stroke-width="1"/>
                <line :x1="shoeCoords.ballX - 4" :y1="shoeCoords.bottomY" :x2="shoeCoords.ballX + 4" :y2="shoeCoords.bottomY" stroke="#e74c3c" stroke-width="1"/>
                <rect :x="shoeCoords.ballX + 8" :y="(shoeCoords.ballTopY + shoeCoords.bottomY) / 2 - 8" width="68" height="16" rx="3" fill="#fff" stroke="#f0c0c0" stroke-width="0.5"/>
                <text :x="shoeCoords.ballX + 42" :y="(shoeCoords.ballTopY + shoeCoords.bottomY) / 2 + 4" text-anchor="middle" class="svg-measure-label red">발볼 {{ m.ball_width }}mm</text>
              </g>
            </svg>

            <div v-if="!hasMeasurements" class="svg-hint">치수를 입력하면 다이어그램에 반영됩니다</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import {
  ClosetClothingRequest,
  CLOTHING_CATEGORY_OPTIONS,
  FIT_FEEDBACK_OPTIONS,
  SUB_CATEGORY_SUGGESTIONS,
  MEASUREMENT_FIELDS,
} from "../class";
import * as closetApi from "../api";

const CLOTHING_MEASURE_HELP: Record<string, string> = {
  total_length: "옷의 맨 위(뒷목점 또는 어깨 꼭대기)에서 밑단까지의 전체 길이입니다. 옷을 평평하게 놓고 세로로 재세요.",
  shoulder_width: "한쪽 어깨 솔기(이음선)에서 반대쪽 어깨 솔기까지의 직선 거리입니다. 옷을 평평하게 펴고 재세요.",
  chest_width: "가슴 부분의 양쪽 옆선(겨드랑이 아래) 사이 거리입니다. 둘레가 아닌 단면(절반) 값이며, ×2를 하면 가슴둘레가 됩니다.",
  sleeve_length: "어깨 솔기(이음선)에서 소매 끝단까지의 길이입니다. 소매를 펴고 일직선으로 재세요.",
  hem_width: "옷의 맨 아래 밑단의 양쪽 끝 사이 거리입니다. 단면(절반) 값이며, ×2를 하면 밑단 둘레가 됩니다.",
  armhole: "한쪽 소매 끝에서 반대쪽 소매 끝까지의 직선 거리입니다. 옷을 평평하게 펴고 양쪽 소매 끝을 일직선으로 재세요.",
  waist_width: "허리 부분의 양쪽 끝 사이 거리입니다. 단면(절반) 값이며, ×2를 하면 허리둘레가 됩니다.",
  hip_width: "엉덩이(힙) 부분의 양쪽 끝 사이 거리입니다. 단면(절반) 값이며, ×2를 하면 힙둘레가 됩니다.",
  thigh_width: "허벅지 부분의 양쪽 끝 사이 거리입니다. 단면(절반) 값이며, ×2를 하면 허벅지둘레가 됩니다.",
  rise: "허리밴드 상단에서 가랑이(밑위 시작점)까지의 세로 길이입니다. 바지의 앞판 중심을 따라 재세요.",
  foot_length: "신발 안쪽의 뒤꿈치부터 앞쪽 가장 긴 부분까지의 직선 거리(mm)입니다.",
  ball_width: "엄지발가락 관절과 새끼발가락 관절을 연결한 가장 넓은 부분의 거리(mm)입니다. 발볼이 넓으면 와이드 사이즈를 권장합니다.",
};

// 기본 치수 (cm)
const TOP_DEFAULTS = { total_length: 70, shoulder_width: 50, chest_width: 52, sleeve_length: 60, hem_width: 50, armhole: 22 };
const BOTTOM_DEFAULTS = { total_length: 100, waist_width: 38, hip_width: 48, thigh_width: 30, rise: 28, hem_width: 18 };
const ONEPIECE_DEFAULTS = { total_length: 100, shoulder_width: 42, chest_width: 46, waist_width: 36, hip_width: 48, sleeve_length: 55 };
const SHOES_DEFAULTS = { foot_length: 265, ball_width: 98 };

const clampScale = (val: number | null | undefined, def: number): number => {
  if (!val) return 1;
  return Math.max(0.5, Math.min(2.0, val / def));
};

export default defineComponent({
  name: "ClosetClothingFormView",
  props: {
    id: { type: [String, Number], default: null },
  },

  setup(props) {
    const router = useRouter();
    const route = useRoute();

    const clothingForm = ref(new ClosetClothingRequest());
    const customSubCategory = ref("");

    const isEditMode = computed(() => !!props.id);

    // 돌아갈 때 유지할 카테고리 필터 (wardrobe에서 넘어온 값)
    const fromCategory = computed(() => (route.query.category as string) || "");
    const wardrobeQuery = () =>
      fromCategory.value ? { category: fromCategory.value } : {};

    const currentSubCategories = computed(() => {
      return SUB_CATEGORY_SUGGESTIONS[clothingForm.value.category] || [];
    });

    const currentMeasureFields = computed(() => {
      return MEASUREMENT_FIELDS[clothingForm.value.category] || [];
    });

    const svgCategory = computed(() => {
      const cat = clothingForm.value.category;
      if (cat === "OUTER") return "TOP";
      return cat;
    });

    // measurements reactive proxy를 직접 반환 (템플릿에서 프로퍼티 접근 시 Vue가 자동 추적)
    const m = computed(() => clothingForm.value.measurements);

    const hasMeasurements = computed(() => {
      const meas = clothingForm.value.measurements;
      return Object.values(meas).some((v) => v != null && Number(v) > 0);
    });

    // ===== TOP/OUTER SVG coords =====
    const topCoords = computed(() => {
      const ms = clothingForm.value.measurements;
      const sw = clampScale(ms.shoulder_width, TOP_DEFAULTS.shoulder_width) * 130;
      const cw = clampScale(ms.chest_width, TOP_DEFAULTS.chest_width) * 140;
      const hw = clampScale(ms.hem_width, TOP_DEFAULTS.hem_width) * 130;
      const tl = clampScale(ms.total_length, TOP_DEFAULTS.total_length) * 240;
      const sl = clampScale(ms.sleeve_length, TOP_DEFAULTS.sleeve_length) * 190;
      const ah = clampScale(ms.armhole, TOP_DEFAULTS.armhole) * 35;

      const cx = 150;
      const topY = 50;
      const bottomY = topY + Math.min(tl, 300);
      const shoulderL = cx - sw / 2;
      const shoulderR = cx + sw / 2;
      const chestL = cx - cw / 2;
      const chestR = cx + cw / 2;
      const hemL = cx - hw / 2;
      const hemR = cx + hw / 2;
      const chestY = topY + Math.min(tl * 0.25, 80);

      // 소매 끝점 — 수평에 가깝게 펼쳐서 직사각형 형태
      const sleeveEndX = Math.min(shoulderR + sl * 0.55, 290);
      const sleeveEndY = Math.min(topY + sl * 0.65, bottomY);
      const sleeveEndLX = Math.max(shoulderL - sl * 0.55, 10);

      // 소매 방향 수직 벡터 (소매 끝 90도 컷)
      const rDx = sleeveEndX - shoulderR;
      const rDy = sleeveEndY - topY;
      const rLen = Math.sqrt(rDx * rDx + rDy * rDy) || 1;
      const rpX = -rDy / rLen;
      const rpY = rDx / rLen;
      // 소매 끝 하단: 수직 ah (90도 컷)
      const rCuffBotX = sleeveEndX + rpX * ah;
      const rCuffBotY = sleeveEndY + rpY * ah;
      // 겨드랑이: 어깨 바로 아래로만 이동, 소매 두께 균일 보정 (ah/rpY)
      const rArmX = shoulderR;
      const rArmY = Math.min(rpY > 0.01 ? topY + ah / rpY : topY + ah, bottomY - 10);

      // 좌측 소매 (대칭)
      const lDx = sleeveEndLX - shoulderL;
      const lDy = sleeveEndY - topY;
      const lLen = Math.sqrt(lDx * lDx + lDy * lDy) || 1;
      const lpX = lDy / lLen;
      const lpY = -lDx / lLen;
      const lCuffBotX = sleeveEndLX + lpX * ah;
      const lCuffBotY = sleeveEndY + lpY * ah;
      // 겨드랑이: 어깨 바로 아래로만 이동, 소매 두께 균일 보정
      const lArmX = shoulderL;
      const lArmY = Math.min(lpY > 0.01 ? topY + ah / lpY : topY + ah, bottomY - 10);

      const leftEdge = Math.min(shoulderL, chestL, hemL, sleeveEndLX, lCuffBotX);

      return {
        topY, bottomY, shoulderL, shoulderR, chestL, chestR, hemL, hemR, chestY,
        sleeveEndX, sleeveEndY, sleeveEndLX,
        rArmX, rArmY, rCuffBotX, rCuffBotY,
        lArmX, lArmY, lCuffBotX, lCuffBotY,
        leftEdge, sw, cw, hw, sl, ah,
      };
    });

    const topSvgPath = computed(() => {
      const c = topCoords.value;
      const cx = 150;
      const neckW = 14;

      return `M ${cx - neckW},${c.topY}
        Q ${cx},${c.topY - 6} ${cx + neckW},${c.topY}
        L ${c.shoulderR},${c.topY}
        L ${c.sleeveEndX},${c.sleeveEndY}
        L ${c.rCuffBotX},${c.rCuffBotY}
        L ${c.rArmX},${c.rArmY}
        L ${c.hemR},${c.rArmY}
        L ${c.hemR},${c.bottomY}
        L ${c.hemL},${c.bottomY}
        L ${c.hemL},${c.lArmY}
        L ${c.lArmX},${c.lArmY}
        L ${c.lCuffBotX},${c.lCuffBotY}
        L ${c.sleeveEndLX},${c.sleeveEndY}
        L ${c.shoulderL},${c.topY}
        Z`;
    });

    // ===== BOTTOM SVG coords =====
    const btmCoords = computed(() => {
      const ms = clothingForm.value.measurements;
      const ww = clampScale(ms.waist_width, BOTTOM_DEFAULTS.waist_width) * 110;
      const hipW = clampScale(ms.hip_width, BOTTOM_DEFAULTS.hip_width) * 130;
      const tw = clampScale(ms.thigh_width, BOTTOM_DEFAULTS.thigh_width) * 60;
      const hmW = clampScale(ms.hem_width, BOTTOM_DEFAULTS.hem_width) * 40;
      const tl = clampScale(ms.total_length, BOTTOM_DEFAULTS.total_length) * 300;
      const riseH = clampScale(ms.rise, BOTTOM_DEFAULTS.rise) * 80;

      const cx = 150;
      const topY = 40;
      const bottomY = topY + Math.min(tl, 350);
      const crotchY = topY + Math.min(riseH, (bottomY - topY) * 0.35);
      const hipY = topY + (crotchY - topY) * 0.5;
      const thighY = crotchY + (bottomY - crotchY) * 0.15;

      const waistL = cx - ww / 2;
      const waistR = cx + ww / 2;
      const hipL = cx - hipW / 2;
      const hipR = cx + hipW / 2;
      const gap = 6;
      // Left leg
      const thighL = cx - gap - tw;
      const thighMid = cx - gap;
      // hem
      const hemL = cx - gap - hmW;
      const hemMid = cx - gap;

      const leftEdge = Math.min(waistL, hipL, thighL, hemL);

      return { topY, bottomY, crotchY, hipY, thighY, waistL, waistR, hipL, hipR, thighL, thighMid, hemL, hemMid, leftEdge, cx, gap, ww, hipW, tw, hmW };
    });

    const bottomSvgPath = computed(() => {
      const c = btmCoords.value;
      const gap = c.gap;
      // Left leg outer/inner, right leg outer/inner
      const lOuterTop = c.hipL;
      const lOuterBot = c.hemL;
      const lInnerBot = c.hemMid;
      const rOuterTop = c.hipR;
      const rOuterBot = c.cx + gap + (c.cx + gap - c.hemMid) + (c.hmW - (c.cx + gap - c.hemMid));
      const rInnerBot = c.cx + gap;
      const rHemOuter = c.cx + gap + c.hmW;

      return `M ${c.waistL},${c.topY}
        L ${c.waistR},${c.topY}
        Q ${c.hipR},${c.hipY} ${c.hipR},${c.hipY}
        L ${c.cx + gap + c.tw},${c.thighY}
        L ${rHemOuter},${c.bottomY}
        L ${rInnerBot},${c.bottomY}
        L ${c.cx + gap},${c.crotchY + 10}
        Q ${c.cx},${c.crotchY} ${c.cx - gap},${c.crotchY + 10}
        L ${c.cx - gap},${c.bottomY}
        L ${lOuterBot},${c.bottomY}
        L ${c.cx - gap - c.tw},${c.thighY}
        Q ${c.hipL},${c.hipY} ${c.hipL},${c.hipY}
        L ${c.waistL},${c.topY}
        Z`;
    });

    // ===== ONEPIECE SVG coords =====
    const opCoords = computed(() => {
      const ms = clothingForm.value.measurements;
      const sw = clampScale(ms.shoulder_width, ONEPIECE_DEFAULTS.shoulder_width) * 110;
      const cw = clampScale(ms.chest_width, ONEPIECE_DEFAULTS.chest_width) * 120;
      const ww = clampScale(ms.waist_width, ONEPIECE_DEFAULTS.waist_width) * 100;
      const hipW = clampScale(ms.hip_width, ONEPIECE_DEFAULTS.hip_width) * 140;
      const tl = clampScale(ms.total_length, ONEPIECE_DEFAULTS.total_length) * 300;
      const sl = clampScale(ms.sleeve_length, ONEPIECE_DEFAULTS.sleeve_length) * 160;

      const cx = 150;
      const topY = 50;
      const bottomY = topY + Math.min(tl, 340);
      const chestY = topY + (bottomY - topY) * 0.18;
      const waistY = topY + (bottomY - topY) * 0.35;
      const hipY = topY + (bottomY - topY) * 0.5;

      const shoulderL = cx - sw / 2;
      const shoulderR = cx + sw / 2;
      const chestL = cx - cw / 2;
      const chestR = cx + cw / 2;
      const waistL = cx - ww / 2;
      const waistR = cx + ww / 2;
      const hipL = cx - hipW / 2;
      const hipR = cx + hipW / 2;
      const sleeveEndX = Math.min(shoulderR + sl * 0.3, 285);
      const sleeveEndY = Math.min(topY + sl * 0.88, waistY);
      const leftEdge = Math.min(shoulderL, chestL, waistL, hipL);

      return { topY, bottomY, chestY, waistY, hipY, shoulderL, shoulderR, chestL, chestR, waistL, waistR, hipL, hipR, sleeveEndX, sleeveEndY, leftEdge, sl };
    });

    const onepieceSvgPath = computed(() => {
      const c = opCoords.value;
      const cx = 150;
      const neckW = 12;
      const sleeveW = 16;
      const slR_x = c.sleeveEndX;
      const slR_y = c.sleeveEndY;
      const slL_x = Math.max(c.shoulderL - c.sl * 0.3, 15);
      const slL_y = slR_y;

      return `M ${cx - neckW},${c.topY}
        Q ${cx},${c.topY - 6} ${cx + neckW},${c.topY}
        L ${c.shoulderR},${c.topY}
        L ${slR_x},${slR_y}
        L ${slR_x - 4},${slR_y + sleeveW}
        L ${c.chestR},${c.chestY + 8}
        L ${c.waistR},${c.waistY}
        L ${c.hipR},${c.hipY}
        Q ${c.hipR + 10},${(c.hipY + c.bottomY) / 2} ${c.hipR + 4},${c.bottomY}
        L ${c.hipL - 4},${c.bottomY}
        Q ${c.hipL - 10},${(c.hipY + c.bottomY) / 2} ${c.hipL},${c.hipY}
        L ${c.waistL},${c.waistY}
        L ${c.chestL},${c.chestY + 8}
        L ${slL_x + 4},${slL_y + sleeveW}
        L ${slL_x},${slL_y}
        L ${c.shoulderL},${c.topY}
        Z`;
    });

    // ===== SHOES SVG coords =====
    const shoeCoords = computed(() => {
      const ms = clothingForm.value.measurements;
      const fl = clampScale(ms.foot_length, SHOES_DEFAULTS.foot_length) * 220;
      const bw = clampScale(ms.ball_width, SHOES_DEFAULTS.ball_width) * 60;

      const heelX = 40;
      const toeX = heelX + Math.min(fl, 250);
      const bottomY = 140;
      const ballTopY = bottomY - Math.min(bw, 100);
      const ballX = heelX + fl * 0.55;

      return { heelX, toeX, bottomY, ballTopY, ballX };
    });

    const shoesSvgPath = computed(() => {
      const c = shoeCoords.value;
      const topY = c.ballTopY - 10;
      const heelTopY = topY + 10;

      return `M ${c.heelX},${c.bottomY}
        L ${c.heelX},${heelTopY}
        Q ${c.heelX},${topY} ${c.heelX + 20},${topY}
        L ${c.ballX - 20},${topY - 10}
        Q ${c.toeX - 10},${topY - 15} ${c.toeX},${c.bottomY - 20}
        Q ${c.toeX + 4},${c.bottomY - 5} ${c.toeX - 6},${c.bottomY}
        Z`;
    });

    const fitClass = (v: string): string => {
      return (
        {
          VERY_TIGHT: "fit-t2",
          TIGHT: "fit-t1",
          PERFECT: "fit-ok",
          LOOSE: "fit-l1",
          VERY_LOOSE: "fit-l2",
        } as Record<string, string>
      )[v] || "";
    };

    const onCategoryChange = () => {
      clothingForm.value.subCategory = "";
      customSubCategory.value = "";
      clothingForm.value.measurements = {};
    };

    const fetchClothing = async () => {
      if (!props.id) return;
      const data = await closetApi.getClothing(Number(props.id));
      if (data) {
        clothingForm.value.bindFromClothing(data);
      }
    };

    const submitClothing = async () => {
      if (!clothingForm.value.category) {
        alert("카테고리를 선택해주세요.");
        return;
      }

      const finalForm = { ...clothingForm.value };
      if (!finalForm.subCategory && customSubCategory.value) {
        finalForm.subCategory = customSubCategory.value;
      }
      if (!finalForm.fitFeedback) finalForm.fitFeedback = null as any;
      if (!finalForm.subCategory) finalForm.subCategory = null as any;

      const cleanMeasurements: Record<string, number> = {};
      Object.entries(finalForm.measurements).forEach(([key, val]) => {
        if (val != null) cleanMeasurements[key] = val as number;
      });
      finalForm.measurements = cleanMeasurements;

      if (isEditMode.value) {
        const data = await closetApi.updateClothing(Number(props.id), finalForm as any);
        if (data?.success) {
          alert("수정되었습니다.");
          router.push({ name: "closet-wardrobe", query: wardrobeQuery() });
        } else {
          alert("수정에 실패했습니다.");
        }
      } else {
        const data = await closetApi.createClothing(finalForm as any);
        if (data?.success) {
          alert("등록되었습니다.");
          router.push({ name: "closet-wardrobe", query: wardrobeQuery() });
        } else {
          alert("등록에 실패했습니다.");
        }
      }
    };

    const goBack = () => {
      router.push({ name: "closet-wardrobe", query: wardrobeQuery() });
    };

    onMounted(() => {
      if (isEditMode.value) {
        fetchClothing();
      } else if (fromCategory.value) {
        // 특정 카테고리에서 등록으로 진입한 경우 해당 카테고리 프리셋
        clothingForm.value.category = fromCategory.value;
      }
    });

    return {
      clothingForm,
      customSubCategory,
      isEditMode,
      currentSubCategories,
      currentMeasureFields,
      categoryOptions: CLOTHING_CATEGORY_OPTIONS,
      fitFeedbackOptions: FIT_FEEDBACK_OPTIONS,
      onCategoryChange,
      submitClothing,
      goBack,
      fitClass,
      CLOTHING_MEASURE_HELP,
      svgCategory,
      m,
      hasMeasurements,
      topCoords,
      topSvgPath,
      btmCoords,
      bottomSvgPath,
      opCoords,
      onepieceSvgPath,
      shoeCoords,
      shoesSvgPath,
    };
  },
});
</script>

<style lang="scss">
@import "../assets/css/closet-clothing.scss";
</style>
